import clsx from 'clsx'

import { useEffect, useMemo, useRef, useState } from 'preact/hooks'
import { useBeaconLogClick } from 'libs/athos/hooks/useBeaconAnalytics'
import { scrollHistoryManager } from 'helpers/scroll-history'
import { KLAVIYO_STAR_RATING_READY_EVENT } from '@/components/custom-elements/klaviyo-star-rating'

import { ResultImage } from 'libs/athos/components/ResultImage'
import { ResultCarousel } from 'libs/athos/components/ResultCarousel'
import { Price } from 'libs/athos/components/Price'
import { useBadges } from '@/components/hooks/useBadges'
import { Badge } from 'libs/athos/components/Badge'
import { Promoline } from 'libs/athos/components/Promoline'
import { usePromolines } from '@/components/hooks/usePromolines'
import { useSwatches } from 'libs/athos/hooks/useSwatches'

const DEFAULT_OPTIONS = {
  hideSwatches: false,
  hidePromolines: false,
  hideBadges: false
}

export function ResultsCard({ result, className, options = DEFAULT_OPTIONS, dataIndex = '0', eager = false, imageSizes, imageWidths, mobileSlider = false }) {
  const [variant] = result.variants

  const finalPrice = variant.price
  const finalCompareAtPrice = variant.compare_at_price

  const {
    hideSwatches = DEFAULT_OPTIONS.hideSwatches,
    hidePromolines = DEFAULT_OPTIONS.hidePromolines,
    hideBadges = DEFAULT_OPTIONS.hideBadges
  } = options
  const { currentResult } = useSwatches(result)
  const clickLogger = useBeaconLogClick()

  const productId = currentResult.parentId ?? currentResult.groupId ?? currentResult.uid

  const productName = currentResult.mfield_utopia_customer_facing_product_name?.trim()
    ? currentResult.mfield_utopia_customer_facing_product_name
    : currentResult.name

  const cardCategory = useMemo(() => {
    const prefix = 'PLPCardCat:'
    const tag = currentResult?.tags?.find((tag) => tag.startsWith(prefix))
    return tag?.slice(prefix.length).trim()
  }, [currentResult?.tags])

  const showSwatchCount = !hideSwatches && result.swatches.length > 0

  const starRatingRef = useRef(null)
  const [hasReviews, setHasReviews] = useState(false)

  useEffect(() => {
    const starRating = starRatingRef.current
    if (!starRating) return

    setHasReviews(Boolean(starRating.querySelector('.kl_reviews__star_rating_widget')))

    const handleReady = () => setHasReviews(true)
    starRating.addEventListener(KLAVIYO_STAR_RATING_READY_EVENT, handleReady)

    return () => starRating.removeEventListener(KLAVIYO_STAR_RATING_READY_EVENT, handleReady)
  }, [productId])

  const criteria = useMemo(() => ({
    tags: currentResult?.tags ?? [],
    collections: currentResult?.collection_handle ?? [],
    products: [currentResult.handle],
    location: 'PLP'
  }), [currentResult?.tags, currentResult?.collection_handle, currentResult?.handle])

  // Metaobject variables
  const allBadges = useBadges(criteria)
  const allPromolines = usePromolines(criteria)

  // Generic function to get first two items with exclusive logic
  const getObjects = (items) => {
    const exclusiveItem = items.find(item => item.exclusive)
    if (exclusiveItem) {
      return [exclusiveItem, null]
    }
    return [items[0] || null, items[1] || null]
  }

  const [firstBadge, secondBadge] = useMemo(() => getObjects(allBadges), [allBadges])
  const [firstPromoline, secondPromoline] = useMemo(() => getObjects(allPromolines), [allPromolines])

  // Creates URL with collection handle reference if it exists
  const url = useMemo(() => {
    return `/products/${currentResult.handle}`
  }, [currentResult])

  // Analytics handling
  const handleLogClick = () => {
    // Collection is a global pulled from template-search.liquid and collection-main.liquid. Ensure value is set in both places :)
    scrollHistoryManager.updateHistoryStateWithCollectionInfo(currentResult)
    clickLogger(currentResult)
  }

  // This is wrapped in an extra div to support injection from Wishlist KING
  // DON'T REMOVE THAT DIV 🔫
  return (
    <div className={clsx('group/card relative flex', className)} id={`result__${currentResult.handle}`} data-dl-track="select_item" data-dl-product-id={productId} data-dl-index={dataIndex}>
      <div className="flex flex-col w-full grow">
        <div className="relative w-full overflow-hidden aspect-1/1">
          <a href={url} onClick={handleLogClick} aria-label={currentResult.name}>
            {mobileSlider && (
              <ResultCarousel className="md:hidden" result={currentResult} eager={eager} sizes={imageSizes} widths={imageWidths} />
            )}
            <ResultImage className={mobileSlider ? 'md-max:hidden' : ''} result={currentResult} eager={eager} sizes={imageSizes} widths={imageWidths}></ResultImage>
            {!hideBadges && (firstBadge || secondBadge) && (
              <div className="flex flex-wrap gap-0.25 absolute top-0.5 left-0.5 w-[calc(100%-1rem)] z-2">
                {firstBadge && <Badge badge={firstBadge} badgeLocation="PLP" product={currentResult} />}
                {secondBadge && <Badge badge={secondBadge} badgeLocation="PLP" product={currentResult} />}
              </div>
            )}
          </a>
          {!hidePromolines && (firstPromoline || secondPromoline) && (
            <div className="flex flex-col gap-0.5 absolute bottom-0.5 left-0.5 w-[calc(100%-1rem)] z-2">
              {firstPromoline && <Promoline promoline={firstPromoline} promolineLocation="PLP" promolineSize="medium" />}
              {secondPromoline && <Promoline promoline={secondPromoline} promolineLocation="PLP" promolineSize="medium" />}
            </div>
          )}
        </div>
        <div className='grow grid xl:grid-cols-full-min auto-rows-min flex-col gap-x-1 gap-y-0.25 px-0.625 lg:px-1 py-1'>
          <a href={url} className="col-[1/2] paragraph-s font-semibold link-fade" aria-label={currentResult.name} dangerouslySetInnerHTML={{ __html: productName }} />
          <Price price={finalPrice} compareAtPrice={finalCompareAtPrice} className="xl:col-[2/3] xl:row-[1/3] flex xl:flex-col gap-x-0.25 gap-y-px xl:text-right" />
          <div className="col-[1/2] flex xl-max:flex-col xl:items-center flex-wrap gap-x-0.25 gap-y-0.5 xl-max:mt-0.25">
            {(cardCategory || showSwatchCount) && (
              <div className="flex items-center gap-x-0.25">
                {cardCategory && (<>
                  <span className="paragraph-xs text-type-black-tertiary" dangerouslySetInnerHTML={{ __html: cardCategory }} />
                  {(showSwatchCount || hasReviews) && <span className="block size-[2.5px] bg-type-black-tertiary rounded-full"></span>}
                </>)}
                {showSwatchCount && (<>
                  <span className="paragraph-xs text-type-black-tertiary">{result.swatches.length} Colour{result.swatches.length !== 1 ? 's' : ''}</span>
                  {hasReviews && <span className="hidden xl:block size-[2.5px] bg-type-black-tertiary rounded-full"></span>}
                </>)}
              </div>
            )}
            <klaviyo-star-rating
              ref={starRatingRef}
              product-id={productId}
              product-title={currentResult.name}
              product-type={currentResult.type}
              disable-interaction=""
              className={String.raw`[&_.kl\_reviews\_\_full\_star]:!size-[14px] [&_.kl\_reviews\_\_partial\_star]:!size-[14px] [&_.kl\_reviews\_\_empty\_star]:!size-[14px] [&_.kl\_reviews\_\_star\_rating\_widget\_\_label]:!hidden xl:-mt-0.125`}
            >
              <div className="klaviyo-star-rating-container flex flex-nowrap items-center">
                <div
                  className="klaviyo-star-rating-widget flex"
                  slot="productReviews"
                  data-id={productId}
                  data-product-title={currentResult.name}
                  data-product-type={currentResult.type}
                ></div>
              </div>
            </klaviyo-star-rating>
          </div>
        </div>
      </div>
    </div>
  )
}
